import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "motion/react";
import {
  Compass,
  ArrowRight,
} from "lucide-react";
import { useCompanyId } from "@/lib/CompanyContext";
import { useIsMobile } from "@/lib/useIsMobile";
import DotGrid from "@/components/reactbits/DotGrid";
import CountUp from "@/components/reactbits/CountUp";
import BlurText from "@/components/reactbits/BlurText";
import FloatingLines from "@/components/reactbits/FloatingLines";
import ShinyText from "@/components/reactbits/ShinyText";
import BorderGlow from "@/components/reactbits/BorderGlow";
import Galaxy from "@/components/reactbits/Galaxy";
import {
  escalaApi,
  type EscalaAssessment,
  type EscalaLetter,
} from "@/api/escala";
import {
  EmptyState,
  PageHeader,
  EscalaRadar,
} from "@/components/ui";
import { EntenderAutofeito } from "@/components/EntenderAutofeito";
import { escalaPct } from "@/lib/escalaScore";

function avgScore(scores: EscalaAssessment["scores"]): number {
  const vals = Object.values(scores);
  if (vals.length === 0) return 0;
  return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
}

/** Resumo curto pro hero: extrai o 1º parágrafo da seção "## Onde você está" do
 *  relatório (profileText) já gerado pela IA no submit. É a leitura qualitativa
 *  do negócio ("você domina X, mas tem dor latente em Y"), pro user bater o olho
 *  e entender antes de scrollar. Fallback pro 1º parágrafo do texto. */
function extractEscalaSummary(profileText: string | null | undefined): string | null {
  if (!profileText) return null;
  const sections = profileText.split(/\n(?=##\s)/);
  const onde = sections.find((s) => /^##\s*onde voc/i.test(s.trim()));
  const source = onde ?? profileText;
  const body = source.replace(/^#{1,6}\s.*$/m, "").trim();
  const firstPara = body.split(/\n\s*\n/)[0]?.trim();
  return firstPara && firstPara.length > 0 ? firstPara : null;
}

// ─────────────────────────────────────────────────────────────
// Sub-views
// ─────────────────────────────────────────────────────────────


const LETTER_NAME_FULL: Record<EscalaLetter, string> = {
  E: "Entender",
  S: "Sistematizar",
  C: "Captar",
  A: "Ativar",
  L: "Lucrar",
  A2: "Acelerar",
};

/** Tira "o que mudou" desde o diagnóstico anterior. */
function EvolutionStrip({
  current,
  previous,
}: {
  current: EscalaAssessment["scores"];
  previous: EscalaAssessment["scores"];
}) {
  const letters = Object.keys(current) as EscalaLetter[];
  // Delta na MESMA escala exibida (0–90), pra bater com o que aparece nas barras.
  const deltas = letters
    .map((l) => ({ l, d: toPct(current[l] ?? 0) - toPct(previous[l] ?? 0) }))
    .filter((x) => x.d !== 0);
  if (deltas.length === 0) return null;
  return (
    <div
      style={{
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
        alignItems: "center",
        fontSize: 12,
        color: "var(--wf-ink-2)",
        padding: "8px 12px",
        background: "#fafaf6",
        border: "1px solid var(--wf-ink-5)",
        borderRadius: 8,
      }}
    >
      <span style={{ fontWeight: 600, color: "var(--wf-ink-3)" }}>desde o último diagnóstico:</span>
      {deltas
        .sort((a, b) => b.d - a.d)
        .map(({ l, d }) => (
          <span key={l} style={{ color: d > 0 ? "#3d7d3d" : "var(--nd-orange-pun)" }}>
            {EscalaRadar.letterChar(l)} {d > 0 ? "+" : ""}
            {d}%
          </span>
        ))}
    </div>
  );
}

/**
 * SixDimensionsBars — KPI visual das 6 dimensões. Cada uma é uma barra
 * vertical animada que enche de baixo pra cima. A linha tracejada em 90%
 * marca "ponto saudável" (a metodologia limita o score em 85, mapeado pra
 * 90% da barra — os últimos 10% são a "exception zone", sempre vazios:
 * a premissa é "sempre dá pra melhorar"). Stagger entre as 6 barras.
 */
const DIMENSION_LETTERS: EscalaLetter[] = ["E", "S", "C", "A", "L", "A2"];
const HEALTHY_TICK_PCT = 90;
// Mapeamento cru(0–100)→exibição(0–90) vive em @/lib/escalaScore pra Painel e
// dashboard mostrarem o MESMO número (gabaritar = 90%, sempre 10% pra evoluir).
const toPct = escalaPct;
const toDisplayScores = (s: EscalaAssessment["scores"]) =>
  Object.fromEntries(
    (Object.entries(s) as Array<[EscalaLetter, number]>).map(([k, v]) => [k, toPct(v)]),
  ) as EscalaAssessment["scores"];

/** Lerp RGB simples entre duas cores. t ∈ [0,1]. */
function lerpRgb(
  c1: [number, number, number],
  c2: [number, number, number],
  t: number,
): [number, number, number] {
  return [
    Math.round(c1[0] + (c2[0] - c1[0]) * t),
    Math.round(c1[1] + (c2[1] - c1[1]) * t),
    Math.round(c1[2] + (c2[2] - c1[2]) * t),
  ];
}

/** Cor por barra: azul (E) → laranja (A2), interpolado linear em RGB. */
const BAR_COLOR_START: [number, number, number] = [59, 111, 255]; // #3b6fff azul
const BAR_COLOR_END: [number, number, number] = [255, 112, 0]; // #ff7000 laranja

function barColorFor(idx: number, total: number): {
  rgb: string;
  rgbLighter: string;
  rgbDarker: string;
  rgbAlpha: (a: number) => string;
} {
  const t = total <= 1 ? 0 : idx / (total - 1);
  const [r, g, b] = lerpRgb(BAR_COLOR_START, BAR_COLOR_END, t);
  // Lighter pra topo do gradient, darker pra base (sutil)
  const mix = (x: number, target: number, k: number) =>
    Math.round(x + (target - x) * k);
  const rl = mix(r, 255, 0.25),
    gl = mix(g, 255, 0.25),
    bl = mix(b, 255, 0.25);
  const rd = mix(r, 0, 0.18),
    gd = mix(g, 0, 0.18),
    bd = mix(b, 0, 0.18);
  return {
    rgb: `rgb(${r}, ${g}, ${b})`,
    rgbLighter: `rgb(${rl}, ${gl}, ${bl})`,
    rgbDarker: `rgb(${rd}, ${gd}, ${bd})`,
    rgbAlpha: (a: number) => `rgba(${r}, ${g}, ${b}, ${a})`,
  };
}

/**
 * RadarShowcase — seção dedicada apenas ao radar, sobre fundo Galaxy (estrelas
 * orange-tinted, sem interatividade do mouse). É a tela "olha o seu negócio".
 */
function RadarShowcase({
  scores,
  weakest,
  strongest,
  previousScores,
}: {
  scores: EscalaAssessment["scores"];
  weakest: EscalaLetter[];
  strongest: EscalaLetter[];
  previousScores: EscalaAssessment["scores"] | null;
}) {
  const isMobile = useIsMobile();
  const size = isMobile ? 300 : 480;
  return (
    <section
      style={{
        position: "relative",
        minHeight: isMobile ? 480 : 700,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: isMobile ? 18 : 28,
        padding: isMobile ? "40px 18px" : "72px 40px",
        overflow: "hidden",
        borderRadius: 24,
        background: "#0b0e1a",
      }}
    >
      {/* Galaxy bg — estrelas brancas + laranjas (mono mix), sem mouse */}
      <Galaxy
        mouseInteraction={false}
        mouseRepulsion={false}
        density={1.2}
        starSpeed={0.35}
        hueShift={25}
        saturation={1.0}
        glowIntensity={0.45}
        twinkleIntensity={0.5}
        rotationSpeed={0.05}
        monoColor
        monoMix={0.6}
        transparent
      />
      {/* Vinheta radial sutil pra dar foco no centro (radar) */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background:
            "radial-gradient(ellipse at center, transparent 35%, rgba(11,14,26,0.55) 100%)",
        }}
      />

      {/* Título — mesma estilização do "Sua maturidade hoje" do hero */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          fontSize: isMobile ? 28 : 40,
          fontWeight: 700,
          lineHeight: 1.05,
          letterSpacing: "-0.02em",
          color: "#ffffff",
          textAlign: "center",
        }}
      >
        <BlurText
          text="Seu radar ESCALA"
          animateBy="words"
          direction="top"
          delay={80}
          className="wf-display"
        />
      </div>

      {/* Radar — wrapper override de --wf-ink* pra letras/grid em branco
          sobre fundo escuro do Galaxy */}
      <div
        style={
          {
            position: "relative",
            zIndex: 1,
            ["--wf-ink" as never]: "#ffffff",
            ["--wf-ink-3" as never]: "rgba(255,255,255,0.7)",
            ["--wf-ink-5" as never]: "rgba(255,255,255,0.18)",
          } as React.CSSProperties
        }
      >
        <EscalaRadar
          scores={toDisplayScores(scores)}
          weakest={weakest}
          strongest={strongest}
          size={size}
          maxValue={100}
          previousScores={previousScores ? toDisplayScores(previousScores) : previousScores}
        />
      </div>
    </section>
  );
}

function SixDimensionsBars({
  scores,
}: {
  scores: EscalaAssessment["scores"];
}) {
  const isMobile = useIsMobile();
  const barHeight = isMobile ? 200 : 260;
  const barWidth = isMobile ? 26 : 34;

  return (
    <section style={{ position: "relative", padding: isMobile ? "32px 22px" : "48px 40px" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(6, 1fr)",
          gap: isMobile ? 6 : 12,
          alignItems: "end",
          padding: isMobile ? "8px 4px 0" : "8px 16px 0",
        }}
      >
        {DIMENSION_LETTERS.map((letter, idx) => {
          // score cru 0–100 → exibição 0–90 (gabaritar deixa sempre 10% de headroom).
          const pct = toPct(scores[letter] ?? 0);
          const fillPct = pct; // a barra enche até no máx 90% (exception zone nos últimos 10%)
          const isHealthy = fillPct >= HEALTHY_TICK_PCT - 0.1;
          const col = barColorFor(idx, DIMENSION_LETTERS.length);
          return (
            <div
              key={letter}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 10,
              }}
            >
              {/* Letra topo */}
              <div
                className="wf-display"
                style={{
                  fontSize: isMobile ? 18 : 22,
                  fontWeight: 800,
                  color: "#ffffff",
                  lineHeight: 1,
                  letterSpacing: "-0.02em",
                }}
              >
                {letter === "A2" ? "A²" : letter}
              </div>

              {/* Track + fill + tick */}
              <div
                style={{
                  position: "relative",
                  width: barWidth,
                  height: barHeight,
                  borderRadius: barWidth,
                  background:
                    "linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.03) 100%)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  overflow: "hidden",
                  boxShadow:
                    "inset 0 1px 2px rgba(0,0,0,0.4), inset 0 -1px 1px rgba(255,255,255,0.04)",
                }}
              >
                {/* Faixa "exception zone" — os últimos 10% (acima do 90%) */}
                <div
                  aria-hidden
                  style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    top: 0,
                    height: `${100 - HEALTHY_TICK_PCT}%`,
                    background:
                      "repeating-linear-gradient(45deg, rgba(255,255,255,0.06) 0 4px, transparent 4px 8px)",
                  }}
                />

                {/* Fill animado — cor interpolada entre azul (E) e laranja (A2) */}
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: `${fillPct}%` }}
                  transition={{
                    delay: 0.15 + idx * 0.09,
                    duration: 1.2,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: `linear-gradient(180deg, ${col.rgbLighter} 0%, ${col.rgb} 60%, ${col.rgbDarker} 100%)`,
                    boxShadow: isHealthy
                      ? `0 0 24px ${col.rgbAlpha(0.6)}`
                      : `0 0 14px ${col.rgbAlpha(0.4)}`,
                  }}
                />

                {/* Tick em 90% (sempre visível, separa "saudável" da "exception zone") */}
                <div
                  aria-hidden
                  style={{
                    position: "absolute",
                    left: -2,
                    right: -2,
                    top: `${100 - HEALTHY_TICK_PCT}%`,
                    borderTop: "1.5px dashed rgba(255,255,255,0.45)",
                    pointerEvents: "none",
                  }}
                />
              </div>

              {/* Score grande */}
              <div
                className="wf-display"
                style={{
                  fontSize: isMobile ? 16 : 20,
                  fontWeight: 800,
                  color: "#ffffff",
                  lineHeight: 1,
                  letterSpacing: "-0.02em",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {pct}%
              </div>

              {/* Nome curto */}
              <div
                style={{
                  fontSize: isMobile ? 10 : 11,
                  color: "#ffffff",
                  textAlign: "center",
                  letterSpacing: "0.02em",
                  whiteSpace: "nowrap",
                }}
              >
                {LETTER_NAME_FULL[letter]}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────

export default function Escala() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const companyId = useCompanyId();

  const latestQuery = useQuery({
    queryKey: ["companies", companyId, "escala", "latest"],
    queryFn: () => escalaApi.latest(companyId!),
    enabled: !!companyId,
  });

  const historyQuery = useQuery({
    queryKey: ["companies", companyId, "escala", "history"],
    queryFn: () => escalaApi.history(companyId!),
    enabled: !!companyId,
    staleTime: 60_000,
  });

  const assessment = latestQuery.data;

  const avg = useMemo(
    () => (assessment ? avgScore(assessment.scores) : 0),
    [assessment],
  );
  const heroSummary = useMemo(
    () => extractEscalaSummary(assessment?.profileText),
    [assessment],
  );

  if (!companyId || latestQuery.isLoading) {
    return (
      <div style={{ padding: 32 }}>
        <span className="wf-hand-note">carregando ESCALA…</span>
      </div>
    );
  }

  // Sem assessment: mostra empty state com CTA pro wizard
  if (!assessment) {
    return (
      <div>
        <PageHeader
          icon={Compass}
          kicker="ESCALA"
          title="Diagnóstico de maturidade"
          maxWidth={760}
        />
        <div style={{ maxWidth: 760, margin: "0 auto", padding: "24px 24px 64px" }}>
          <EmptyState
            icon={Compass}
            title="Vamos diagnosticar sua maturidade ESCALA"
            description="30 perguntas em 8 etapas (15-20 min). Avaliação de 6 dimensões: Entender, Sistematizar, Captar, Ativar, Lucrar, Acelerar. O resultado contextualiza seus agentes em todas as tarefas."
            action={
              <button
                type="button"
                onClick={() => navigate("/escala/wizard")}
                className="wf-btn wf-btn--primary"
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                Começar diagnóstico <ArrowRight size={13} color="#fff" />
              </button>
            }
          />
        </div>
      </div>
    );
  }

  const weakest = assessment.weakestLetters ?? [];
  const strongest = assessment.strongestLetters ?? [];
  // Diagnóstico anterior (pra mostrar evolução) — o 2º mais recente do histórico.
  const previousAssessment = (historyQuery.data ?? [])
    .filter((a) => a.id !== assessment.id)
    .sort((a, b) => b.completedAt.localeCompare(a.completedAt))[0] ?? null;

  const completedDate = new Date(assessment.completedAt).toLocaleDateString(
    "pt-BR",
    { day: "2-digit", month: "long", year: "numeric" },
  );

  return (
    <div style={{ position: "relative", minHeight: "100%" }}>
      {/* Grid de bolinhas sutil de fundo — escopado ao container da página
          (não fixo no viewport) pra não atravessar o menu lateral nem o
          topbar do Shell. */}
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 0 }}>
        <DotGrid dotSize={1.8} gap={26} baseColor="#a8a59a" opacity={0.55} />
      </div>

      <div style={{ position: "relative", zIndex: 1 }}>
        <div
          style={{
            maxWidth: 1180,
            margin: "0 auto",
            padding: isMobile ? "28px 18px 64px" : "40px 32px 80px",
            display: "flex",
            flexDirection: "column",
            gap: 40,
          }}
        >
          {/* Hero — kicker + título + número gigante + constelação,
              sobre LightPillar (raios laranja+azul ND) */}
          <div
            style={{
              position: "relative",
              overflow: "hidden",
              borderRadius: 24,
              padding: isMobile ? "32px 22px" : "56px 44px",
              minHeight: isMobile ? 420 : 480,
              border: "1px solid var(--wf-ink-5)",
              background: "#0b0e1a",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <FloatingLines
              linesGradient={["#ff7000", "#ff9a3c", "#5a7dff", "#3b6fff"]}
              interactive={false}
              parallax={false}
              animationSpeed={0.9}
              mixBlendMode="screen"
            />
            {/* Vinheta escura discreta à esquerda pra texto contrastar com os
                raios; mantém o card 100% escuro do outro lado (sem fade-to-white). */}
            <div
              aria-hidden
              style={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                background:
                  "linear-gradient(90deg, rgba(11,14,26,0.75) 0%, rgba(11,14,26,0.25) 35%, rgba(11,14,26,0) 55%)",
              }}
            />
            <div
              style={{
                position: "relative",
                zIndex: 1,
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr minmax(320px, 420px)",
                gap: isMobile ? 28 : 40,
                alignItems: "center",
              }}
            >
            {/* Lado esquerdo: kicker, título, número, model, action */}
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "rgba(255,255,255,0.55)",
                  textTransform: "uppercase",
                  letterSpacing: "0.12em",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Compass size={12} color="var(--nd-orange)" />
                Diagnóstico ESCALA · {completedDate}
              </div>
              <div
                style={{
                  fontSize: isMobile ? 28 : 40,
                  fontWeight: 700,
                  lineHeight: 1.05,
                  letterSpacing: "-0.02em",
                  color: "#ffffff",
                }}
              >
                <BlurText
                  text="Sua maturidade hoje"
                  animateBy="words"
                  direction="top"
                  delay={80}
                  className="wf-display"
                />
              </div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 4, marginTop: 4 }}>
                <CountUp
                  to={toPct(avg)}
                  duration={1.2}
                  className="wf-display"
                  style={{
                    fontSize: isMobile ? 88 : 140,
                    fontWeight: 800,
                    color: "#ffffff",
                    lineHeight: 0.9,
                    letterSpacing: "-0.04em",
                    fontVariantNumeric: "tabular-nums",
                  }}
                />
                <span
                  className="wf-display"
                  style={{
                    fontSize: isMobile ? 56 : 88,
                    fontWeight: 800,
                    color: "var(--nd-orange)",
                    lineHeight: 0.9,
                    letterSpacing: "-0.04em",
                  }}
                >
                  %
                </span>
              </div>
              {heroSummary && (
                <p
                  style={{
                    marginTop: 14,
                    maxWidth: 560,
                    fontSize: isMobile ? 14 : 15.5,
                    lineHeight: 1.55,
                    color: "rgba(255,255,255,0.82)",
                    fontFamily: "var(--wf-font-body)",
                  }}
                >
                  {heroSummary}
                </p>
              )}
              {previousAssessment && (
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                  <EvolutionStrip
                    current={assessment.scores}
                    previous={previousAssessment.scores}
                  />
                </div>
              )}
              <div style={{ marginTop: 10 }}>
                <button
                  type="button"
                  onClick={() => navigate("/escala/wizard")}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 13,
                    fontFamily: "var(--wf-font-body)",
                    fontWeight: 600,
                    padding: "9px 18px",
                    borderRadius: 999,
                    background:
                      "linear-gradient(135deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.04) 100%)",
                    border: "1px solid rgba(255,255,255,0.22)",
                    color: "rgba(255,255,255,0.95)",
                    cursor: "pointer",
                    backdropFilter: "blur(10px)",
                    transition:
                      "border-color 180ms ease, background 180ms ease, box-shadow 180ms ease",
                    boxShadow: "0 0 0 0 rgba(255,112,0,0)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor =
                      "rgba(255,112,0,0.55)";
                    e.currentTarget.style.background =
                      "linear-gradient(135deg, rgba(255,112,0,0.18) 0%, rgba(59,111,255,0.10) 100%)";
                    e.currentTarget.style.boxShadow =
                      "0 0 24px 0 rgba(255,112,0,0.18)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor =
                      "rgba(255,255,255,0.22)";
                    e.currentTarget.style.background =
                      "linear-gradient(135deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.04) 100%)";
                    e.currentTarget.style.boxShadow =
                      "0 0 0 0 rgba(255,112,0,0)";
                  }}
                >
                  <ShinyText text="Refazer diagnóstico" speed={4} />
                </button>
              </div>
            </div>

            {/* Lado direito: vazio de propósito — os raios do LightPillar
                vêm pra cá. A leitura visual fica no número à esquerda + os
                raios à direita, sem viz competindo. */}
            </div>
          </div>

          <BorderGlow animated borderRadius={24}>
            <SixDimensionsBars scores={assessment.scores} />
          </BorderGlow>

          <RadarShowcase
            scores={assessment.scores}
            weakest={weakest}
            strongest={strongest}
            previousScores={previousAssessment?.scores ?? null}
          />

          <EntenderAutofeito companyId={companyId} />

        </div>
      </div>
    </div>
  );
}
